import { ChevronRight, Dices, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminKey } from "@/hooks/use-auth";
import type { CameraSpec, GenerateMazeInput, Maze, MazePreview } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { cn } from "@/lib/utils";

import { viewBox } from "./maze-geometry";
import { FLOOR, MarkerLegend, Markers, Walls } from "./maze-svg";

/**
 * Camera presets. The original camera is 320x240 with a 120 degree horizontal field of view
 * (`fx = 160 / tan(60deg) = 92`). The other presets keep that field of view at a higher
 * resolution; "custom" exposes the intrinsics directly.
 */
const CAMERA_PRESETS: { id: string; label: string; camera: Partial<CameraSpec> | null }[] = [
  { id: "default", label: "320×240, original", camera: null },
  { id: "640", label: "640×480, same field of view", camera: fov(640, 480) },
  { id: "960", label: "960×720, same field of view", camera: fov(960, 720) },
  { id: "custom", label: "Custom intrinsics…", camera: null },
];
const CAMERA_ITEMS: Record<string, string> = Object.fromEntries(CAMERA_PRESETS.map((p) => [p.id, p.label]));

function fov(width: number, height: number, hfovDeg = 120): Partial<CameraSpec> {
  const fx = width / 2 / Math.tan((hfovDeg * Math.PI) / 360);
  return { width, height, fx, fy: fx, cx: width / 2, cy: height / 2 };
}

/** Grid sizes; `31` is what every cohort so far has run in (`maze.py`'s `Maze(31, 31)`). */
const SIZES = [15, 21, 31, 41, 51] as const;
const SIZE_ITEMS: Record<string, string> = Object.fromEntries(
  SIZES.map((n) => [String(n), `${n} × ${n}${n === 31 ? "  (standard)" : ""}`]),
);

type Params = Required<Omit<GenerateMazeInput, "camera" | "name" | "notes">>;

/**
 * The generator's defaults, which are `maze.py`'s: 31×31, a room started in 10% of the cells
 * it visits, rooms up to 3×3, target 4-12 m from the spawn. Only the seed varies by default;
 * everything else is under "Advanced" because nobody has needed to change it yet.
 */
const DEFAULTS: Omit<Params, "seed"> = {
  rows: 31,
  cols: 31,
  room_density: 0.1,
  max_room_size: 3,
  target_min_distance: 4,
  target_max_distance: 12,
};

/**
 * 53 random bits: the largest integer JSON carries exactly from here. The maze is a pure
 * function of the seed, so the seed is the secret; a five-digit one is a maze anyone with
 * the generator finds by counting. It is shown in the form so it can be copied into notes,
 * never into a name.
 */
const randomSeed = () => {
  const [hi, lo] = crypto.getRandomValues(new Uint32Array(2));
  return (hi % 2 ** 21) * 2 ** 32 + lo;
};

/**
 * Add a maze to the library, watching it take shape.
 *
 * Every change to the parameters asks the server what `generate` would produce and draws it
 * on the right; nothing is written until "Create". Generation is deterministic, so the maze
 * created is exactly the one on screen. The seed is the whole description, and rerolling it
 * is the fastest way to browse for a layout one likes.
 *
 * A maze is immutable once made, which is why the camera is chosen here and nowhere else:
 * every frame a student ever sees of this maze is rendered with it, and changing it later
 * would invalidate their model. The camera does not affect the layout, so it is not part of
 * the preview.
 *
 * Sized to fit the viewport without scrolling: the preview is a square of at most 64svh and
 * the form is kept short enough to stand beside it.
 */
function sameParams(a: Params, b: Params): boolean {
  return (Object.keys(a) as (keyof Params)[]).every((key) => a[key] === b[key]);
}

export function NewMazeDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (maze: Maze) => void;
}) {
  // Only reachable from admin pages, which already gate on the key.
  const adminKey = useAdminKey() ?? "";
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [form, setForm] = useState<Params>(() => ({ ...DEFAULTS, seed: randomSeed() }));
  const [preset, setPresetState] = useState("default");
  const setPreset = (value: string | null) => setPresetState(value ?? "default");
  const [custom, setCustom] = useState<Partial<CameraSpec>>(fov(320, 240));
  const [advanced, setAdvanced] = useState(false);

  const [previewed, setPreviewed] = useState<{ params: Params; maze: MazePreview } | null>(null);
  const preview = previewed?.maze ?? null;
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const inflight = useRef<AbortController | null>(null);

  // Debounced and cancellable: typing "31" must not draw the 3-row maze, and a slow answer
  // for an old set of parameters must not overwrite a newer one.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      inflight.current?.abort();
      inflight.current = controller;
      setPreviewing(true);
      try {
        const next = await adminApi.previewMaze(form, adminKey, controller.signal);
        if (controller.signal.aborted) return;
        setPreviewed({ params: form, maze: next });
        setPreviewError(null);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setPreviewError(caught instanceof ApiError ? caught.message : "Preview failed");
      } finally {
        if (!controller.signal.aborted) setPreviewing(false);
      }
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form, adminKey, open]);

  const camera = preset === "custom" ? custom : (CAMERA_PRESETS.find((p) => p.id === preset)?.camera ?? null);

  async function create() {
    setBusy(true);
    try {
      const created = await adminApi.generateMaze({ ...form, name, camera }, adminKey);
      toast.success(`Added "${created.name}"`);
      onCreated(created);
      onOpenChange(false);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  const set = <K extends keyof Params>(key: K, value: number) => setForm((prev) => ({ ...prev, [key]: value }));
  const square = form.rows === form.cols && SIZES.includes(form.rows as (typeof SIZES)[number]);

  // Create only what is on screen: inside the debounce window `previewing` is still false
  // while the form has already moved on, so compare the parameters too.
  const ready =
    previewed !== null &&
    previewError === null &&
    !previewing &&
    sameParams(previewed.params, form);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-lg:max-h-[92svh] max-lg:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New maze</DialogTitle>
          <DialogDescription>
            The same seed gives the same maze on every machine, so the seed stays between admins.
            Nothing is saved until you create it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          {/* ---- form ---- */}
          <div className="flex flex-col gap-4">
            <Row label="Name" htmlFor="m-name">
              <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Named after its id if left empty" />
            </Row>

            <Row label="Seed" htmlFor="m-seed">
              <div className="flex gap-1.5">
                <Input id="m-seed" type="number" min={0} value={form.seed} onChange={(e) => set("seed", Number(e.target.value))} className="font-mono" />
                <Button variant="outline" size="icon" onClick={() => set("seed", randomSeed())} title="Another seed" aria-label="Another seed">
                  <RefreshCw className="size-4" aria-hidden />
                </Button>
              </div>
            </Row>

            <Row label="Size">
              <Select
                items={SIZE_ITEMS}
                value={square ? String(form.rows) : null}
                onValueChange={(v) => {
                  if (v) setForm((prev) => ({ ...prev, rows: Number(v), cols: Number(v) }));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={`${form.rows} × ${form.cols}`} />
                </SelectTrigger>
                <SelectContent>
                  {SIZES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {SIZE_ITEMS[String(n)]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Row>

            <Row label="Camera" hint="Fixed for the life of the maze: every frame a student sees is rendered with it.">
              <Select items={CAMERA_ITEMS} value={preset} onValueChange={setPreset}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMERA_PRESETS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {preset === "custom" ? (
                <div className="grid grid-cols-3 gap-2 pt-1">
                  {(["width", "height", "fx", "fy", "cx", "cy"] as const).map((key) => (
                    <Small key={key} id={`cam-${key}`} label={key} value={custom[key] ?? 0} onChange={(v) => setCustom((prev) => ({ ...prev, [key]: v }))} />
                  ))}
                </div>
              ) : null}
            </Row>

            <Collapsible open={advanced} onOpenChange={setAdvanced} className="mt-auto">
              <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
                <ChevronRight className={cn("size-3.5 transition-transform", advanced && "rotate-90")} aria-hidden />
                Advanced
              </CollapsibleTrigger>
              <CollapsibleContent className="grid grid-cols-2 gap-x-2 gap-y-3 pt-3">
                <Small id="m-rows" label="Rows" value={form.rows} step={2} min={15} max={99} onChange={(v) => set("rows", v)} />
                <Small id="m-cols" label="Cols" value={form.cols} step={2} min={15} max={99} onChange={(v) => set("cols", v)} />
                <Small id="m-rd" label="Room density" value={form.room_density} step={0.05} min={0} max={1} onChange={(v) => set("room_density", v)} />
                <Small id="m-mr" label="Max room size" value={form.max_room_size} min={1} max={15} onChange={(v) => set("max_room_size", v)} />
                <Small id="m-tmin" label="Target ≥ (m)" value={form.target_min_distance} step={0.5} min={0} onChange={(v) => set("target_min_distance", v)} />
                <Small id="m-tmax" label="Target ≤ (m)" value={form.target_max_distance} step={0.5} min={0} onChange={(v) => set("target_max_distance", v)} />
              </CollapsibleContent>
            </Collapsible>
          </div>

          {/* ---- preview ---- */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative aspect-square w-full max-w-[min(64svh,32rem)]">
              {preview ? (
                <svg
                  viewBox={viewBox(preview.grid)}
                  className={cn("size-full rounded-md border select-none transition-opacity duration-200", (previewing || previewError) && "opacity-50")}
                  style={{ background: FLOOR }}
                  role="img"
                  aria-label="Preview of the maze these parameters generate"
                >
                  <Walls grid={preview.grid} />
                  <Markers grid={preview.grid} spawn={preview.spawn.cell} target={preview.target.cell} />
                </svg>
              ) : (
                <Skeleton className="size-full rounded-md" />
              )}
              {previewing || (!preview && !previewError) ? (
                <div className="bg-background/90 text-muted-foreground absolute top-2 right-2 flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs shadow-sm">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  {preview ? "Updating" : "Generating"}
                </div>
              ) : null}
              {previewError ? (
                <div className="absolute inset-x-4 bottom-4 rounded-md border border-destructive/40 bg-background/95 p-2 text-destructive text-xs shadow-sm">{previewError}</div>
              ) : null}
            </div>
            <div className="text-muted-foreground flex w-full max-w-[min(64svh,32rem)] flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <MarkerLegend />
              {preview ? (
                <span className="ml-auto font-mono">
                  {preview.grid.free_cells} free cells · target {distance(preview).toFixed(1)} m
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={create} disabled={busy || !ready}>
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Dices className="size-4" aria-hidden />}
            Create this maze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Straight-line spawn-to-target distance in metres, the quantity the min/max constraint is on. */
function distance(p: MazePreview): number {
  return Math.hypot(p.target.x - p.spawn.x, p.target.y - p.spawn.y);
}

function Row({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-muted-foreground text-xs leading-snug">{hint}</p> : null}
    </div>
  );
}

function Small({
  id,
  label,
  value,
  onChange,
  step,
  min,
  max,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <div className="grid gap-1">
      <Label htmlFor={id} className="text-muted-foreground text-xs font-normal">
        {label}
      </Label>
      <Input id={id} type="number" value={value} step={step} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} className="h-8 font-mono text-xs" />
    </div>
  );
}
